/**
 * #731 — the position card's row BOUNDARY. Sibling of `OrderCardRow`; same
 * reasoning, same contract. See that file's header for why this is a component
 * rather than a keyed handler cache.
 *
 * `highlighted` is a plain boolean for the same reason: the same-game map is
 * rebuilt every publication by design, so passing anything derived from its
 * identity would rebuild every row for a highlight that did not move.
 */
import { memo, useCallback, type Dispatch, type SetStateAction } from 'react';
import { CompactPositionItem } from '../../components/cards/PositionCard';
import { cardAnnotationsFor, useCardAnnotationsActive } from '../../components/cards/CardAnnotations';
import type { PositionCardVM } from '../../components/cards/positionCardModel';
import type { EditPositionData } from './EditPositionModal';
import { PolyCloseControl, type PolyCloseState } from './PolyCloseControl';
import type { SameGameRowRef } from './index';
import { NO_US_DISPATCH, UsCloseControl, usPinOriginOf, type UsRowDispatch } from './usTrackerRows';

export interface PositionCardRowProps {
  vm: PositionCardVM;
  highlighted: boolean;
  hidePnl: boolean;
  showRestingCloseCount?: boolean;
  divergence: React.ComponentProps<typeof CompactPositionItem>['divergence'];
  divergenceFlash: boolean;
  detailHref: string | null;
  pinned: boolean;
  /** Derived once by the parent so the pin entry cannot disagree with the header. */
  label: string;
  /** #828 — stable row identity for the reading-position primitive. Reaches
   *  `CompactPositionItem` through the `...cardProps` rest spread. */
  viewAnchor?: string;
  setHoveredRow: Dispatch<SetStateAction<SameGameRowRef | null>>;
  openOrderPanel: (ticker: string, side: 'yes' | 'no' | null | undefined) => void;
  setEditPosition: Dispatch<SetStateAction<EditPositionData | null>>;
  togglePin: (entry: { key: string; kind: 'position'; label: string; us?: { accountId: string; view: 'own' | 'desk' } }) => void;
  /** #1006 — a Polymarket position's Close, as one plain state so the row
   *  still compares equal when it didn't move; absent for Kalshi. */
  polyClose?: PolyCloseState;
  /** Stable: asks the Tracker's confirm dialog for the position with this key. */
  requestPolyClose?: (key: string) => void;
  /** #1226 — a Polymarket US row's handlers (stable); the row draws its Close from `vm.us`. */
  usDispatch?: UsRowDispatch;
}

function PositionCardRowInner({
  vm,
  label,
  setHoveredRow,
  openOrderPanel,
  setEditPosition,
  togglePin,
  polyClose,
  requestPolyClose,
  usDispatch = NO_US_DISPATCH,
  ...cardProps
}: PositionCardRowProps) {
  // #979: fill the annotations slot only on a page that shows annotations.
  const annotated = useCardAnnotationsActive();
  const onMouseEnter = useCallback(
    () => setHoveredRow({ venue: vm.venue, ticker: vm.ticker, key: vm.key }),
    [setHoveredRow, vm.venue, vm.ticker, vm.key],
  );
  const onMouseLeave = useCallback(() => setHoveredRow(null), [setHoveredRow]);
  const us = vm.us;
  const onOpen = useCallback(() => {
    if (us) usDispatch.open({ accountId: us.accountId, marketSlug: us.marketSlug, outcome: us.outcome });
    else openOrderPanel(vm.ticker, vm.side);
  }, [openOrderPanel, vm.ticker, vm.side, us, usDispatch]);
  const onEdit = useCallback(
    () =>
      setEditPosition({
        ticker: vm.ticker,
        side: vm.side,
        position: vm.qty,
        // A Kalshi position always carries its average; the null is a US row's (#1226), which has no Edit.
        averagePrice: vm.avgCents ?? 0,
        currentPriceCents: vm.nowCents,
      }),
    [setEditPosition, vm.ticker, vm.side, vm.qty, vm.avgCents, vm.nowCents],
  );
  const onUsClose = useCallback(() => { if (us) usDispatch.close(us); }, [us, usDispatch]);
  const onTogglePin = useCallback(
    () => togglePin({ key: vm.key, kind: 'position', label, ...usPinOriginOf(us) }),
    [togglePin, vm.key, label, us],
  );
  const onPolyClose = useCallback(() => requestPolyClose?.(vm.key), [requestPolyClose, vm.key]);

  return (
    <CompactPositionItem
      vm={vm}
      {...cardProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onOpen={onOpen}
      onEdit={onEdit}
      onTogglePin={onTogglePin}
      close={us ? <UsCloseControl facts={us} onClose={onUsClose} />
        : polyClose ? <PolyCloseControl state={polyClose} onClose={onPolyClose} /> : undefined}
      annotations={
        annotated ? cardAnnotationsFor({ kind: 'position', venue: vm.venue, marketId: vm.ticker, outcome: vm.side }) : undefined
      }
    />
  );
}

export const PositionCardRow = memo(PositionCardRowInner);
